import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, FREQ_LABEL } from "./constants";

// All state + API handlers for the Horário Espiritual page.
export function useSpiritualSchedule() {
  const today = new Date();
  // Coming back from an email "mark done" link: ?marked=done|notfound|invalid&year=&month=
  const initialParams = new URLSearchParams(window.location.search);
  const [year, setYear] = useState(parseInt(initialParams.get("year")) || today.getFullYear());
  const [month, setMonth] = useState(parseInt(initialParams.get("month")) || today.getMonth() + 1);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exame, setExame] = useState("");
  const [exameDirty, setExameDirty] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [assessors, setAssessors] = useState([]);
  const [sharedIds, setSharedIds] = useState([]);
  const [legacyShareAll, setLegacyShareAll] = useState(false);
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(true);
  const [monthlyRecapEnabled, setMonthlyRecapEnabled] = useState(true);
  const [editTitle, setEditTitle] = useState(null); // { vinculoKey, practiceId, value }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, p, asx] = await Promise.all([
        axios.get(`${API}/members/spiritual/schedule?year=${year}&month=${month}`, { withCredentials: true }),
        axios.get(`${API}/members/spiritual/alerts`, { withCredentials: true }),
        axios.get(`${API}/members/me/profile`, { withCredentials: true }),
        axios.get(`${API}/members/spiritual/assessors`, { withCredentials: true }),
      ]);
      setSchedule(s.data);
      setExame(s.data.exame_particular || "");
      setExameDirty(false);
      setAlerts(a.data.items || []);
      const list = asx.data?.items || [];
      setAssessors(list);
      const explicit = Array.isArray(p.data?.shared_with_assessor_ids) ? p.data.shared_with_assessor_ids : [];
      const legacyAll = !!p.data?.share_spiritual_with_assessor;
      setLegacyShareAll(legacyAll);
      setWeeklyDigestEnabled(p.data?.weekly_exam_digest_enabled !== false);
      setMonthlyRecapEnabled(p.data?.monthly_recap_enabled !== false);
      // If legacy "share with all" is on and no explicit list, expand to all current assessors for UI clarity.
      setSharedIds(explicit.length ? explicit : (legacyAll ? list.map((a2) => a2.id) : []));
    } catch (e) {
      toast.error("Erro ao carregar.");
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  };

  const saveExame = async () => {
    try {
      await axios.put(`${API}/members/spiritual/schedule/exame?year=${year}&month=${month}`, { exame_particular: exame }, { withCredentials: true });
      setExameDirty(false);
      toast.success("Exame particular guardado.");
    } catch { toast.error("Erro a guardar."); }
  };

  const addPractice = async (vinculoKey) => {
    const title = window.prompt("Nova prática:");
    if (!title || !title.trim()) return;
    try {
      const r = await axios.post(`${API}/members/spiritual/schedule/practice?year=${year}&month=${month}`,
        { title: title.trim(), vinculo_key: vinculoKey }, { withCredentials: true });
      setSchedule((s) => ({
        ...s,
        vinculos: s.vinculos.map((v) => v.key === vinculoKey
          ? { ...v, practices: [...v.practices, r.data] } : v),
      }));
    } catch { toast.error("Erro a adicionar."); }
  };

  const removePractice = async (vinculoKey, practiceId) => {
    if (!window.confirm("Remover esta prática? (perde-se também a marcação dos dias)")) return;
    try {
      await axios.delete(`${API}/members/spiritual/schedule/practice/${practiceId}?year=${year}&month=${month}&vinculo_key=${vinculoKey}`,
        { withCredentials: true });
      setSchedule((s) => ({
        ...s,
        vinculos: s.vinculos.map((v) => v.key === vinculoKey
          ? { ...v, practices: v.practices.filter((p) => p.id !== practiceId) } : v),
      }));
    } catch { toast.error("Erro a remover."); }
  };

  const updateCell = async (vinculoKey, practiceId, day, value) => {
    // Optimistic
    setSchedule((s) => ({
      ...s,
      vinculos: s.vinculos.map((v) => v.key !== vinculoKey ? v : {
        ...v, practices: v.practices.map((p) => p.id !== practiceId ? p : {
          ...p,
          days: value === "" ? Object.fromEntries(Object.entries(p.days || {}).filter(([k]) => k !== String(day)))
            : { ...(p.days || {}), [String(day)]: value },
        }),
      }),
    }));
    try {
      await axios.patch(`${API}/members/spiritual/schedule/cell?year=${year}&month=${month}`,
        { vinculo_key: vinculoKey, practice_id: practiceId, day, value: value || null },
        { withCredentials: true });
    } catch { toast.error("Erro a guardar."); load(); }
  };

  const cycleCell = (vinculoKey, practice, day) => {
    // Cycle: empty → done → skip → empty
    const current = practice.days?.[day] || "";
    const next = current === "" ? "done" : current === "done" ? "skip" : "";
    updateCell(vinculoKey, practice.id, day, next);
  };

  const copyPrevious = async () => {
    try {
      const r = await axios.post(`${API}/members/spiritual/schedule/copy-previous?year=${year}&month=${month}`, {}, { withCredentials: true });
      if (r.data.copied > 0) {
        toast.success(`${r.data.copied} prática(s) copiada(s) do mês anterior.`);
        load();
      } else {
        toast.info("Nada novo para copiar — estas práticas já existem neste mês.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a copiar do mês anterior.");
    }
  };

  const savePracticeTitle = async () => {
    if (!editTitle) return;
    const { vinculoKey, practiceId, value } = editTitle;
    const t = value.trim();
    setEditTitle(null);
    if (!t) return;
    // Optimistic update
    setSchedule((s) => ({
      ...s,
      vinculos: s.vinculos.map((v) => v.key !== vinculoKey ? v : {
        ...v, practices: v.practices.map((p) => p.id !== practiceId ? p : { ...p, title: t }),
      }),
    }));
    try {
      await axios.patch(
        `${API}/members/spiritual/schedule/practice/${practiceId}?year=${year}&month=${month}&vinculo_key=${vinculoKey}`,
        { title: t },
        { withCredentials: true },
      );
      toast.success("Prática atualizada.");
    } catch {
      toast.error("Erro a atualizar o título.");
      load();
    }
  };

  const setFrequency = async (vinculoKey, practice, next) => {
    if (next === (practice.frequency || "daily")) return;
    // Optimistic update
    setSchedule((s) => ({
      ...s,
      vinculos: s.vinculos.map((v) => v.key !== vinculoKey ? v : {
        ...v, practices: v.practices.map((p) => p.id !== practice.id ? p : { ...p, frequency: next }),
      }),
    }));
    try {
      await axios.patch(
        `${API}/members/spiritual/schedule/practice/${practice.id}?year=${year}&month=${month}&vinculo_key=${vinculoKey}`,
        { frequency: next },
        { withCredentials: true },
      );
      toast.success(`Frequência: ${FREQ_LABEL[next]}`);
      load();  // refresh dashboard
    } catch {
      toast.error("Erro a alterar frequência.");
      load();
    }
  };

  const toggleWeeklyDigest = async (v) => {
    setWeeklyDigestEnabled(v);
    try {
      await axios.patch(`${API}/members/me/profile`, { weekly_exam_digest_enabled: v }, { withCredentials: true });
      toast.success(v ? "Digest semanal ativado." : "Digest semanal desativado.");
    } catch {
      toast.error("Erro a guardar.");
      setWeeklyDigestEnabled(!v);
    }
  };

  const toggleMonthlyRecap = async (v) => {
    setMonthlyRecapEnabled(v);
    try {
      await axios.patch(`${API}/members/me/profile`, { monthly_recap_enabled: v }, { withCredentials: true });
      toast.success(v ? "Resumo mensal ativado." : "Resumo mensal desativado.");
    } catch {
      toast.error("Erro a guardar.");
      setMonthlyRecapEnabled(!v);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/members/spiritual/schedule/pdf?year=${year}&month=${month}`,
        { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumo-espiritual-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro a gerar o PDF.");
    }
  };

  const toggleAssessor = async (assessorId, on) => {
    const next = on
      ? Array.from(new Set([...sharedIds, assessorId]))
      : sharedIds.filter((x) => x !== assessorId);
    const prev = sharedIds;
    setSharedIds(next);
    try {
      await axios.patch(`${API}/members/me/profile`, {
        shared_with_assessor_ids: next,
        // Turn off legacy "all assessors" flag once user picks explicitly.
        share_spiritual_with_assessor: next.length > 0,
      }, { withCredentials: true });
      setLegacyShareAll(next.length > 0 && next.length === assessors.length && legacyShareAll);
      toast.success(on ? "Partilhado com o Assessor." : "Partilha removida.");
    } catch {
      toast.error("Erro a guardar partilha.");
      setSharedIds(prev);
    }
  };

  return {
    year, month, schedule, loading, alerts, assessors, sharedIds,
    exame, setExame, exameDirty, setExameDirty,
    weeklyDigestEnabled, monthlyRecapEnabled,
    editTitle, setEditTitle,
    load, prevMonth, nextMonth, saveExame, addPractice, removePractice,
    cycleCell, copyPrevious, savePracticeTitle, setFrequency,
    toggleWeeklyDigest, toggleMonthlyRecap, downloadPdf, toggleAssessor,
  };
}
